import { createRouter, createWebHistory } from 'vue-router';

import InternalRoutes from './internal';
import ExternalRoutes from './external';
import ScalemaRoutes from './internal/scalema';
import PersonalBoardRoutes from './internal/personal-board';
import ProfileRoutes from './internal/profile';
import PageNotFoundRoute from './404';
import GlobalModalRoutes from './internal/global-modals';
import Printable from './printable';
import ReleaseNotes from './release-notes';
import JobPostings from './job-postings';
import Resumes from './resumes';
import UnsubscribeEmail from './unsubscribe-email';
import Notifications from './internal/notifications';
import Redirect from './redirect';
import store from 'source/store';

/* Notifications is transferred to main router routes due to internal routes
 requiring user accounts to have a company id and workforce id/s

 For reference please see src/index.js
*/

/* NavigationDuplicated console error fix for router.push menthod
 Only throw an error if it's not a NavigationDuplicated error

 ** Kindly refer to the link below for more**
 https://github.com/vuejs/vue-router/issues/2872
*/
// const originalPush = VueRouter.prototype.push;
// const originalReplace = VueRouter.prototype.replace;

// VueRouter.prototype.push = function push(location) {
//   return originalPush.call(this, location).catch(err => {
//     if (!err.name.includes('NavigationDuplicated')) {
//       throw err;
//     }
//   });
// };

// // Same fix for router.replace method
// VueRouter.prototype.replace = function replace(location) {
//   return originalReplace.call(this, location).catch(err => {
//     if (!err.name.includes('NavigationDuplicated')) {
//       throw err;
//     }
//   });
// };

const routerHistory = createWebHistory();

const routes = [
  ...PersonalBoardRoutes,
  ...InternalRoutes,
  ...ExternalRoutes,
  ...ScalemaRoutes,
  ...ProfileRoutes,
  PageNotFoundRoute,
  ...Printable,
  ...ReleaseNotes,
  JobPostings,
  Resumes,
  ...UnsubscribeEmail,
  ...Notifications,
  Redirect,
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('source/pages/auth/NotFoundPage.vue'),
    meta: {
        title: 'HQZen',
        canDisplayGlobalModals: false,
    },
},
];

const allowedRoutes = [
  'personal/',
  'home/',
  '/scalema',
  '/profile',
  '/help-center',
  '/concerns',
  'employment-contract',
  'landing-page'
];

const newBusinessUserAllowedRoutes = [
  'home/home-page',
  '/profile',
  '/help-center',
  'personal/account-information'
];

async function populateRoutes(modal, routes) {
  for (let route of routes) {
    let modalRoute = {
      ...modal,
      name: route.name + '-' + modal.name,
    };
    if (route.children) {
      populateRoutes(modal, route.children);
      if (route?.meta?.canDisplayGlobalModals !== false) {
        route.children.push(modalRoute);
      }
    } else if (route.meta.canDisplayGlobalModals !== false) {
      route['children'] = [modalRoute];
    }
  }
}

for (let modal of GlobalModalRoutes) {
  populateRoutes(modal, routes);
}

const router = createRouter({
  history: routerHistory,
  routes,
  scrollBehavior (to, from, savedPosition) {
    if (savedPosition) return savedPosition;

    if (!to.hash) {
      const audienceRoutes = ['landing-page', 'landing-page-applicants'];

      if (audienceRoutes.includes(to.name) && to.name !== from.name) {
        return { top: 0 };
      }

      return;
    }

    /* This runs for in-page `<a href="#x">` clicks too. The browser's own
       fragment scroll does not survive the router's history handling, so the
       router is what actually moves the page — do not "simplify" this by
       deferring to native behaviour, which scrolls nothing at all here.

       This branch read `selector: to.hash` — Vue Router 3 syntax. Router 4
       ignores the unknown key, leaving `top`/`left` undefined, so it
       smooth-scrolled to 0: every hash navigation in the app, including the
       bookings cards' `$router.push({ hash })`, has silently gone to the top
       of the page instead of to its target. Invisible until the landing page
       opted into `scroll-behavior: smooth`, which made the wrong scroll
       visible instead of instantaneous.

       Router 4 scrolls to explicit coordinates (getElementPosition in
       vue-router), which bypasses `scroll-margin-top`. Read it off the target
       and pass it as the offset, so each page keeps its own spacing rather
       than this shared router hardcoding one page's header height. The element
       is passed directly, not as a `#id` string, because Router 4 puts string
       selectors through querySelector, which needs ids escaped. */
    const element = document.getElementById(to.hash.slice(1));

    if (!element) return;

    return {
      el: element,
      top: parseFloat(getComputedStyle(element).scrollMarginTop) || 0,
      behavior: 'smooth',
    };
  }
});

router.beforeEach((to, from, next) => {
    const authToken = localStorage.getItem('auth_token');
    const isOnAdminLeave = localStorage.getItem('is-on-admin-leave');
    const signInRoute = router.options.routes.find(route => route.path === '/signin');
    const user = store.state.auth.user;
    const is_new_business_user = JSON.parse(localStorage.getItem('is_new_business_user'))
    // redirect for new business users
    if (is_new_business_user && !newBusinessUserAllowedRoutes.some(route => {
        return to.path.includes(route) || to.name.includes(route);
    })) {
        let showPaywall = to.name != 'home-page'
        next({ name: 'home-page', query: { paywall: showPaywall }, replace: true });
    }

    if (authToken && isOnAdminLeave === 'true'
      && !allowedRoutes.some(route => {
      return to.path.includes(route) || to.name.includes(route);
    })) {
      next({ name: 'personal-board' });
    }

    // If there is a redirection link this will store it as a sign in metadata
    if (!to.name.includes('signin')) {
      signInRoute.meta.path = to.fullPath;
    }

    if (to.matched.length) {
        document.title = to.meta.title;
    }

    if (to.name === 'recruitment-posting'&&
        to.params.job_posting_pk && to.params.job_posting_title) {
        document.title = `Posting #${to.params.job_posting_pk}: ${to.params.job_posting_title}`;
    }


    if (to.name === 'recruitment-send-job-offer' || to.name === 'template-maker') {
        return next();
    } else {

        // clear localStorage Data for jobOffer, employmentPolicy, employmentRule
        localStorage.removeItem('jobOffer');
        localStorage.removeItem('employmentPolicy');
        localStorage.removeItem('contractPolicy');
        localStorage.removeItem('employmentRule');
        return next();
    }
});

export default router;
